import type React from "react";
import { useMemo } from "react";

import type { FeelingAtHomeQuestionResponseDto, FeelingAtHomeSurveyQuestionV2Dto } from "@/api/types";
import { formatDate } from "@/components/FormattedDate/FormattedDate";
import type { ModalBaseProps } from "@/components/Modal/Modal";
import { Modal } from "@/components/Modal/Modal";
import { User, UserTooltipContent } from "@/components/User/User";
import { RatingBadge } from "@/design-system/RatingBadge";
import { Stack } from "@/design-system/Stack";
import { createDashboardColumnHelper, TABLE_COLUMN_SIZES } from "@/design-system/Table/helpers";
import { Table } from "@/design-system/Table/Table";
import { TABLE_KEYS, useResizableTable } from "@/hooks/useResizableTable";
import { useTranslation } from "@/translations";

type FahHistoryQuestionResponsesModalProps = ModalBaseProps & {
  order: number;
  question: FeelingAtHomeSurveyQuestionV2Dto;
};
export function FahHistoryQuestionResponsesModal({
  order,
  question,
  isOpened,
  onOpenChange,
}: FahHistoryQuestionResponsesModalProps): React.ReactNode {
  const { i18n, t } = useTranslation();

  const totalResponses = question.responseCount;
  const columns = useMemo(() => {
    const helper = createDashboardColumnHelper<FeelingAtHomeQuestionResponseDto>();

    return [
      helper.accessor("rating", {
        header: t("component.surveys-question.details.table.header.rating"),
        cell: (cell) => <RatingBadge rating={cell.getValue()} outOf={10} />,
        size: TABLE_COLUMN_SIZES.STATUS.DEFAULT,
        minSize: TABLE_COLUMN_SIZES.STATUS.MIN,
      }),
      helper.accessor("answer", {
        header: t("component.surveys-question.details.table.header.open"),
        cell: (cell) => <span className="block py-0.5 wrap-break-word">{cell.getValue()}</span>,
        meta: { shouldTruncate: false },
        minSize: TABLE_COLUMN_SIZES.LEADING_CONTENT.MIN,
        size: TABLE_COLUMN_SIZES.LEADING_CONTENT.DEFAULT,
      }),
      helper.accessor("user", {
        header: t("component.surveys-question.details.table.header.username"),
        cell: (cell) => <User user={cell.getValue()} size="sm" />,
        meta: {
          renderTruncatedTooltip: (cell) => {
            const data = cell.getValue();

            return <UserTooltipContent fullName={data.fullName} />;
          },
        },
        size: TABLE_COLUMN_SIZES.USER.DEFAULT,
        minSize: TABLE_COLUMN_SIZES.USER.MIN,
      }),
      helper.accessor("answeredAt", {
        header: t("component.surveys-question.details.table.header.date"),
        cell: (cell) => <span>{formatDate(i18n, "datetimeShort", cell.getValue())}</span>,
        size: TABLE_COLUMN_SIZES.DATE.DEFAULT,
        minSize: TABLE_COLUMN_SIZES.DATE.MIN,
      }),
    ];
  }, [i18n, t]);
  const tableInstance = useResizableTable(TABLE_KEYS.FAH_QUESTION_RESPONSES, {
    columns,
    data: question.responses,
    enableSorting: false,
  });

  return (
    <Modal.Root
      size="fullscreen"
      title={`${order}. ${question.ratingQuestionText}`}
      description={question.openQuestionText}
      {...{
        isOpened,
        onOpenChange,
      }}
    >
      <Modal.Body>
        <Stack gap="sm">
          <span className="text-caption">
            {t("common.entity.response", {
              count: totalResponses || 0,
            })}
          </span>
          {totalResponses > 0 && <Table table={tableInstance} data-testid="survey-answer-list" />}
        </Stack>
      </Modal.Body>
    </Modal.Root>
  );
}
